import { buildMatchingContext } from "./matchingContext";
import { evaluateRule } from "./ruleEvaluator";

const validationFor = (question) => (
  question?.validation_rules
  || question?.validation
  || {}
);

const consentValue = (value) => (
  value && typeof value === "object" && "consent_text" in value
    ? value.consent_text
    : value
);

const choiceDisplayValues = (choice) => {
  if (choice !== null && choice !== undefined && typeof choice !== "object") {
    return [String(choice)];
  }
  if (!choice) return [];
  return [choice.text, choice.label, choice.value, choice.id]
    .filter((value) => value !== undefined && value !== null)
    .map(String);
};

const isDisqualifyingChoice = (choice) => (
  choice
  && typeof choice === "object"
  && (
    choice.disqualifies === true
    || choice.is_disqualifying === true
    || choice.isDisqualifying === true
  )
);

const configuredChoiceMatch = (
  question,
  responseValue,
  allowTopLevelDqChoices = false,
) => {
  const choices = Array.isArray(question?.answer_choices)
    ? question.answer_choices
    : [];
  const declaredDqChoices = allowTopLevelDqChoices
    && Array.isArray(question?.dqChoices)
    ? question.dqChoices.map(String)
    : [];
  const selectedValues = consentValue(responseValue);
  const selected = Array.isArray(selectedValues)
    ? selectedValues.map(String)
    : [selectedValues].filter((value) => value !== undefined && value !== null).map(String);

  return choices.find((choice) => (
    (
      isDisqualifyingChoice(choice)
      || choiceDisplayValues(choice).some((value) => declaredDqChoices.includes(value))
    )
    && choiceDisplayValues(choice).some((value) => selected.includes(value))
  ));
};

const firstLegacyMatch = (validation, responseValue) => {
  const value = consentValue(responseValue);
  if (
    validation.disqualifying_answer !== undefined
    && validation.disqualifying_answer === value
  ) {
    return value;
  }
  const configured = Array.isArray(validation.disqualifying_answers)
    ? validation.disqualifying_answers
    : [];
  const selected = Array.isArray(value) ? value : [value];
  return selected.find((item) => configured.includes(item));
};

const answerLabel = (value) => {
  const normalized = consentValue(value);
  if (Array.isArray(normalized)) return normalized.join(", ");
  if (normalized && typeof normalized === "object") {
    return normalized.label || normalized.value || "the selected answer";
  }
  return String(normalized ?? "the selected answer");
};

export const shouldEvaluateDisqualificationLocally = ({
  hasTreatmentAuthority,
}) => !hasTreatmentAuthority;

export const evaluateQuestionDisqualification = ({
  question,
  questionnaire,
  responses,
  responseValue,
  allowTopLevelDqChoices = false,
}) => {
  if (!question) return { disqualified: false };
  const validation = validationFor(question);
  const canonicalRule = validation.disqualifier;
  const context = buildMatchingContext(
    questionnaire,
    responses,
    question,
  );
  const canonicalMatch = (
    canonicalRule
    && evaluateRule(canonicalRule, context)
  );
  const legacyMatch = firstLegacyMatch(validation, responseValue);
  const choiceMatch = configuredChoiceMatch(
    question,
    responseValue,
    allowTopLevelDqChoices,
  );
  if (!canonicalMatch && legacyMatch === undefined && !choiceMatch) {
    return { disqualified: false };
  }

  const matchedAnswer = (
    choiceMatch
      ? consentValue(responseValue)
      : (legacyMatch === undefined ? responseValue : legacyMatch)
  );
  return {
    disqualified: true,
    reason: (
      `You selected "${answerLabel(matchedAnswer)}", which makes you `
      + "ineligible for this treatment at this time."
    ),
    questionText: question.question_text,
    disqualifyingAnswer: matchedAnswer,
    questionId: question.id,
    disqualifyingCondition: "question_disqualifier",
  };
};
